import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { FieldFormulaBase, FormulaFieldSlot } from '../field-formula.base';
import {
  AGGREGATE_FUNCTIONS,
  DATE_PARTS,
  DateToken,
  FormulaToken,
  canAddClosingParenthesis,
  canAddOpeningParenthesis,
  dateFunctionFor,
  isOperatorTurn,
  newDateToken,
  operatorsAround,
} from '../formula';
import { canAddDateFunction, tokenText } from '../formula-typing';
import { FormulaStatusComponent } from '../formula-status/formula-status.component';

export interface AutocompleteStep {
  index: number;
  slot: 'field' | 'part' | 'second' | 'third';
}

export interface FormulaSuggestion {
  id: string;
  label: string;
  hint?: string;
  apply: () => void;
}

export interface FormulaSuggestionGroup {
  label: string;
  items: FormulaSuggestion[];
}

export interface FormulaPlaceholder {
  key: string;
  params?: Record<string, string>;
}

const NUMBER = /^\d+(\.\d+)?$/;
const SYMBOLS = ['+', '-', '*', '/', '(', ')'];

@Component({
  selector: 'cipo-field-formula-autocomplete',
  imports: [
    FlexLayoutModule,
    FormulaStatusComponent,
    MatAutocompleteModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
  ],
  templateUrl: './formula-autocomplete.component.html',
  styleUrl: './formula-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FieldFormulaBase, useExisting: forwardRef(() => FieldFormulaAutocompleteComponent) }],
})
export class FieldFormulaAutocompleteComponent extends FieldFormulaBase {
  private readonly translate = inject(TranslateService);
  private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');
  private readonly trigger = viewChild(MatAutocompleteTrigger);

  readonly query = signal('');
  readonly step = signal<AutocompleteStep>(null);

  readonly groups = computed<FormulaSuggestionGroup[]>(() => {
    const tokens = this.tokens();
    const step = this.step();
    const query = this.query().trim().toLowerCase();
    let groups = this.operandGroups(tokens);
    if (step) {
      groups = this.stepGroups(step, tokens[step.index] as DateToken);
    } else if (isOperatorTurn(tokens)) {
      groups = this.operatorGroups(tokens);
    }
    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(
          item =>
            item.id === 'number' ||
            !query ||
            item.label.toLowerCase().includes(query) ||
            (item.hint ?? '').toLowerCase().includes(query),
        ),
      }))
      .filter(group => group.items.length);
  });

  readonly operatorTurn = computed(() => !this.step() && isOperatorTurn(this.tokens()));

  readonly placeholder = computed<FormulaPlaceholder>(() => {
    const tokens = this.tokens();
    const step = this.step();
    if (step) {
      return { key: this.stepLabel(step, tokens[step.index] as DateToken) };
    }
    if (!this.operatorTurn()) {
      return { key: 'modules.fieldConfig.formula.typeOperand' };
    }
    const symbols = this.operatorGroups(tokens).flatMap(group => group.items.map(item => item.id));
    const operators = symbols.filter(symbol => symbol !== ')').join(' ');
    if (!operators) {
      return { key: '' };
    }
    const key = symbols.includes(')') ? 'typeOperatorOrClose' : 'typeOperator';
    return { key: `modules.fieldConfig.formula.${key}`, params: { operators } };
  });

  chipText(token: FormulaToken): string {
    return tokenText(token, part => this.translate.instant(`modules.fieldConfig.formula.dateParts.${part}`));
  }

  isSymbol(token: FormulaToken): boolean {
    return token.type === 'operator' || token.type === 'parenthesis';
  }

  choose(event: MatAutocompleteSelectedEvent): void {
    (event.option.value as FormulaSuggestion).apply();
    this.clearQuery();
    this.reopen();
  }

  onInput(input: HTMLInputElement): void {
    if (this.operatorTurn()) {
      input.value = '';
      return;
    }
    this.query.set(input.value);
  }

  onKeydown(event: KeyboardEvent): void {
    const text = this.query().trim();
    if (event.key === 'Escape' && this.step()) {
      this.step.set(null);
      return;
    }
    if (event.key === 'Backspace') {
      // The chip input has already moved the focus to the last chip: it comes back, so typing goes on.
      if (!this.query() && !event.repeat && this.tokens().length) {
        this.input()?.nativeElement.focus();
        this.remove(this.tokens().length - 1);
      }
      return;
    }
    if (event.key === ' ' && NUMBER.test(text)) {
      event.preventDefault();
      this.applySuggestion('number');
      return;
    }
    if (SYMBOLS.includes(event.key)) {
      event.preventDefault();
      if (NUMBER.test(text)) {
        this.applySuggestion('number');
      }
      this.applySuggestion(event.key);
    }
  }

  /** The last chip goes alone, as typing back would; a chip in the middle takes what keeps the formula valid. */
  remove(index: number): void {
    if (index === this.tokens().length - 1) {
      this.deleteAt(index);
    } else {
      this.removeAt(index);
    }
    this.step.set(null);
    this.reopen();
  }

  resume(index: number): void {
    const slot = this.missingSlot(this.tokens()[index]);
    if (slot) {
      this.step.set({ index, slot });
      this.reopen();
    }
  }

  // MARK: suggestions

  private operandGroups(tokens: FormulaToken[]): FormulaSuggestionGroup[] {
    const groups: FormulaSuggestionGroup[] = [];
    const dateResult = this.isDateResult();
    if (!dateResult) {
      groups.push(
        this.numberGroup(value => this.append({ type: 'number', value })),
        {
          label: 'modules.fieldConfig.formula.groups.fields',
          items: this.numericFields().map(field => ({
            id: `field-${field.key}`,
            label: field.value,
            hint: this.functionLabel(AGGREGATE_FUNCTIONS[0].key),
            apply: () => this.append({ type: 'function', functionKey: AGGREGATE_FUNCTIONS[0].key, field }),
          })),
        },
        {
          label: 'modules.fieldConfig.formula.groups.functions',
          items: (this.numericFields().length ? AGGREGATE_FUNCTIONS : []).map(entry => ({
            id: `function-${entry.key}`,
            label: `${entry.label}(…)`,
            hint: this.translate.instant(`modules.fieldConfig.formula.help.${entry.value}`),
            apply: () => this.startStep({ type: 'function', functionKey: entry.key }, 'field'),
          })),
        },
      );
    }
    if (canAddDateFunction(tokens) && this.dateFields().length) {
      const name = dateFunctionFor(dateResult);
      groups.push({
        label: 'modules.fieldConfig.formula.groups.dateFunctions',
        items: [
          {
            id: name,
            label: `${name}(…)`,
            hint: this.translate.instant(`modules.fieldConfig.formula.help.${name}`),
            apply: () => this.startStep(newDateToken(dateResult), 'part'),
          },
        ],
      });
    }
    if (!dateResult && canAddOpeningParenthesis(tokens)) {
      groups.push(this.symbolGroup([{ value: '(', label: '(' }]));
    }
    return groups;
  }

  private operatorGroups(tokens: FormulaToken[]): FormulaSuggestionGroup[] {
    if (this.isDateResult()) {
      return [];
    }
    // A copy: `operatorsAround` can hand back the shared OPERATORS list itself.
    const symbols = operatorsAround(tokens, tokens.length).map(({ value, label }) => ({
      value: value as string,
      label,
    }));
    if (canAddClosingParenthesis(tokens)) {
      symbols.push({ value: ')', label: ')' });
    }
    return [this.symbolGroup(symbols)];
  }

  private stepGroups(step: AutocompleteStep, token: DateToken): FormulaSuggestionGroup[] {
    const label = this.stepLabel(step, token);
    if (step.slot === 'part') {
      return [
        {
          label,
          items: DATE_PARTS.map(part => ({
            id: part,
            label: this.translate.instant(`modules.fieldConfig.formula.dateParts.${part}`),
            apply: () => {
              this.setDatePart(step.index, part);
              this.step.set({ index: step.index, slot: 'second' });
            },
          })),
        },
      ];
    }
    const slot = step.slot as FormulaFieldSlot;
    const next = slot === 'second' ? { index: step.index, slot: 'third' as const } : null;
    const groups = [
      {
        label,
        items: this.fieldsFor(slot, token).map(field => ({
          id: `field-${field.key}`,
          label: field.value,
          apply: () => {
            this.setField(step.index, slot, field);
            this.step.set(next);
          },
        })),
      },
    ];
    if (step.slot === 'second' && token.secondKind === 'number') {
      groups.unshift(
        this.numberGroup(value => {
          this.setSecondNumber(step.index, value);
          this.step.set(next);
        }),
      );
    }
    return groups;
  }

  private stepLabel(step: AutocompleteStep, token: DateToken): string {
    switch (step.slot) {
      case 'field':
        return 'modules.fieldConfig.formula.selectColumn';
      case 'part':
        return 'modules.fieldConfig.formula.groups.dateParts';
      case 'second':
        return token.secondKind === 'date'
          ? 'modules.fieldConfig.formula.selectStartDate'
          : 'modules.fieldConfig.formula.selectNumber';
      default:
        return token.secondKind === 'date'
          ? 'modules.fieldConfig.formula.selectEndDate'
          : 'modules.fieldConfig.formula.selectDate';
    }
  }

  /** The number being typed, offered first so Enter, Space or an operator takes it. */
  private numberGroup(apply: (value: number) => void): FormulaSuggestionGroup {
    const text = this.query().trim();
    const value = Number(text);
    return {
      label: 'modules.fieldConfig.formula.number',
      items: NUMBER.test(text) && value ? [{ id: 'number', label: text, apply: () => apply(value) }] : [],
    };
  }

  private symbolGroup(symbols: { value: string; label: string }[]): FormulaSuggestionGroup {
    return {
      label: 'modules.fieldConfig.formula.groups.operators',
      items: symbols.map(symbol => ({
        id: symbol.value,
        label: symbol.label,
        apply: () =>
          this.append(
            symbol.value === '(' || symbol.value === ')'
              ? { type: 'parenthesis', value: symbol.value }
              : ({ type: 'operator', value: symbol.value } as FormulaToken),
          ),
      })),
    };
  }

  // MARK: private

  private startStep(token: FormulaToken, slot: AutocompleteStep['slot']): void {
    this.append(token);
    this.step.set({ index: this.tokens().length - 1, slot });
  }

  private applySuggestion(id: string): void {
    const suggestion = this.groups()
      .flatMap(group => group.items)
      .find(item => item.id === id);
    if (suggestion) {
      suggestion.apply();
      this.clearQuery();
    }
  }

  private missingSlot(token: FormulaToken): AutocompleteStep['slot'] {
    if (token.type === 'function') {
      return token.field ? null : 'field';
    }
    if (token.type === 'date') {
      const usesField = token.secondKind === 'date' || token.useSecondField;
      if (!(usesField ? token.secondField : token.secondNumber)) {
        return 'second';
      }
      return token.thirdField ? null : 'third';
    }
    return null;
  }

  private clearQuery(): void {
    this.query.set('');
    const input = this.input()?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  /**
   * Puts the caret back in the input and opens the list. Deferred: once a removed chip is gone, the chip grid
   * hands the focus to its neighbour, and the autocomplete closes its panel only after the selection event.
   */
  private reopen(): void {
    setTimeout(() => {
      this.input()?.nativeElement.focus();
      this.trigger()?.openPanel();
    });
  }
}
